import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield, ShieldAlert, Ban, Trash2, RefreshCw, Clock, AlertTriangle,
  Plus, CheckCircle2, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

function formatDT(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function timeUntil(iso) {
  if (!iso) return 'permanent';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function SummaryCard({ icon: Icon, label, value, tone = 'cyan', testId }) {
  const tones = {
    cyan: 'text-[#00F0FF] border-[#00F0FF]/30',
    red: 'text-red-400 border-red-500/40',
    amber: 'text-amber-400 border-amber-500/40',
    green: 'text-green-400 border-green-500/40',
  };
  return (
    <div
      data-testid={testId}
      className={`bg-neutral-950 border ${tones[tone]} rounded-xl p-5`}
    >
      <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-wider">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className={`mt-2 text-3xl font-semibold ${tones[tone].split(' ')[0]}`}>
        {value}
      </div>
    </div>
  );
}

export default function AdminSecurityBlockedIPs() {
  const [summary, setSummary] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [events, setEvents] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const [newIp, setNewIp] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newExpiryHours, setNewExpiryHours] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    try {
      const [s, b, e, w] = await Promise.all([
        axios.get(`${API_URL}/api/admin/security/summary`, authHeaders()),
        axios.get(`${API_URL}/api/admin/security/blocked-ips?active_only=${showActiveOnly}`, authHeaders()),
        axios.get(`${API_URL}/api/admin/security/ip-activity?limit=50`, authHeaders()),
        axios.get(`${API_URL}/api/admin/security/whitelist`, authHeaders()),
      ]);
      setSummary(s.data);
      setBlocked(b.data.blocked_ips || []);
      setEvents(e.data.events || []);
      setWhitelist(w.data.whitelist || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [showActiveOnly]);

  const handleBlock = async (e) => {
    e.preventDefault();
    if (!newIp.trim() || !newReason.trim()) {
      toast.error('IP and reason are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/security/blocked-ips`,
        {
          ip: newIp.trim(),
          reason: newReason.trim(),
          expires_in_hours: newExpiryHours ? parseInt(newExpiryHours, 10) : null,
        },
        authHeaders()
      );
      toast.success(`Blocked ${newIp.trim()}`);
      setNewIp('');
      setNewReason('');
      setNewExpiryHours('');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block IP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async (ip) => {
    if (!window.confirm(`Unblock ${ip}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/security/blocked-ips/${encodeURIComponent(ip)}`,
        authHeaders()
      );
      toast.success(`Unblocked ${ip}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unblock IP');
    }
  };

  const badgeForSource = (source) => {
    if (source === 'auto') {
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    }
    if (source === 'security_monitor') {
      return 'bg-red-500/20 text-red-300 border-red-500/40';
    }
    return 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/40';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-neutral-400" data-testid="security-loading">
          Loading security data…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-10 space-y-8" data-testid="admin-security-blocked-ips">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-white flex items-center gap-3">
              <Shield className="w-7 h-7 text-[#00F0FF]" />
              IP Blocklist & Abuse Detection
            </h1>
            <p className="text-neutral-400 mt-2 max-w-2xl text-sm">
              Manually block malicious IPs and monitor auto-blocks from the
              sliding-window abuse detector. Auto-blocks trigger on excessive
              auth requests, write-endpoint hammering, 404 scanners, and
              401/403 storms. <b className="text-neutral-200">Repeat offenders</b>
              {' '}— any IP that racks up 4+ auto-blocks in 7 days gets
              escalated to a <b className="text-red-300">PERMANENT</b> block
              automatically, so mobile/shared IPs self-heal after one burst but
              persistent attackers stay locked out for good.
            </p>
          </div>
          <Button
            onClick={loadAll}
            variant="outline"
            className="border-neutral-700 text-white hover:bg-neutral-800"
            data-testid="security-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            icon={Ban}
            label="Currently Blocked"
            value={summary?.active_blocked ?? 0}
            tone="red"
            testId="summary-active-blocked"
          />
          <SummaryCard
            icon={Activity}
            label="Auto-Blocks 24h"
            value={summary?.auto_blocked_24h ?? 0}
            tone="amber"
            testId="summary-auto-24h"
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Denied Reqs 24h"
            value={summary?.denied_requests_24h ?? 0}
            tone="amber"
            testId="summary-denied-24h"
          />
          <SummaryCard
            icon={CheckCircle2}
            label="Total Ever Blocked"
            value={summary?.total_blocked ?? 0}
            tone="cyan"
            testId="summary-total-blocked"
          />
        </div>

        {/* Manual Block Form */}
        <form
          onSubmit={handleBlock}
          className="bg-neutral-950 border border-neutral-800 rounded-xl p-6 space-y-4"
          data-testid="block-ip-form"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#00F0FF]" />
            Manually block an IP
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase text-neutral-500 tracking-wider">IP Address</label>
              <Input
                data-testid="block-ip-input"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder="e.g. 203.0.113.42"
                className="bg-black border-neutral-700 text-white mt-1"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-neutral-500 tracking-wider">Reason</label>
              <Input
                data-testid="block-reason-input"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="e.g. Repeated brute-force attempts on /login"
                className="bg-black border-neutral-700 text-white mt-1"
                required
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase text-neutral-500 tracking-wider">Expires in (hours, optional)</label>
              <Input
                data-testid="block-expiry-input"
                type="number"
                min="1"
                value={newExpiryHours}
                onChange={(e) => setNewExpiryHours(e.target.value)}
                placeholder="Leave empty for permanent"
                className="bg-black border-neutral-700 text-white mt-1"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80 font-semibold"
                data-testid="block-submit-btn"
              >
                <Ban className="w-4 h-4 mr-2" />
                {submitting ? 'Blocking…' : 'Block This IP'}
              </Button>
            </div>
          </div>
        </form>

        {/* Blocked IPs Table */}
        <div className="bg-neutral-950 border border-neutral-800 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-neutral-800">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              Blocked IPs ({blocked.length})
            </h2>
            <label className="text-xs text-neutral-400 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                data-testid="active-only-toggle"
                className="accent-[#00F0FF]"
              />
              Active only
            </label>
          </div>
          {blocked.length === 0 ? (
            <div className="p-8 text-center text-neutral-500 text-sm" data-testid="blocked-empty-state">
              No {showActiveOnly ? 'active ' : ''}blocked IPs. Good news.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-neutral-500 text-xs uppercase tracking-wider bg-neutral-900/60">
                  <tr>
                    <th className="text-left px-4 py-3">IP</th>
                    <th className="text-left px-4 py-3">Reason</th>
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-left px-4 py-3">Blocked By</th>
                    <th className="text-left px-4 py-3">Blocked At</th>
                    <th className="text-left px-4 py-3">Expires</th>
                    <th className="text-right px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {blocked.map((row) => (
                    <tr
                      key={row.ip}
                      className="text-neutral-200 hover:bg-neutral-900/50"
                      data-testid={`blocked-row-${row.ip}`}
                    >
                      <td className="px-4 py-3 font-mono text-white">
                        {row.ip}
                        {row.source === 'auto' && row.strike_count_7d > 0 && (
                          <span
                            className={`ml-2 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                              row.strike_count_7d > row.strike_threshold
                                ? 'text-red-300 border-red-500/50 bg-red-500/10'
                                : row.strike_count_7d === row.strike_threshold
                                ? 'text-amber-300 border-amber-500/50 bg-amber-500/10'
                                : 'text-neutral-400 border-neutral-700 bg-neutral-800'
                            }`}
                            title={`${row.strike_count_7d} auto-blocks in the last 7 days. Next strike (${row.strike_threshold + 1}) becomes permanent.`}
                            data-testid={`strike-badge-${row.ip}`}
                          >
                            {row.strike_count_7d > row.strike_threshold
                              ? 'REPEAT OFFENDER'
                              : `${row.strike_count_7d}/${row.strike_threshold} STRIKES`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate" title={row.reason}>
                        {row.reason}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs border ${badgeForSource(row.source)}`}>
                          {row.source}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-400">{row.blocked_by || '—'}</td>
                      <td className="px-4 py-3 text-neutral-400">{formatDT(row.created_at)}</td>
                      <td className="px-4 py-3 text-neutral-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {row.expires_at ? timeUntil(row.expires_at) : 'permanent'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleUnblock(row.ip)}
                          className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-xs"
                          data-testid={`unblock-btn-${row.ip}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="bg-neutral-950 border border-neutral-800 rounded-xl">
          <div className="p-4 border-b border-neutral-800">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#00F0FF]" />
              Recent Activity (auto-blocks & denied requests)
            </h2>
          </div>
          {events.length === 0 ? (
            <div className="p-8 text-center text-neutral-500 text-sm" data-testid="activity-empty-state">
              No recent activity.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-800" data-testid="activity-list">
              {events.map((ev, i) => (
                <li key={i} className="p-4 flex items-start gap-3 text-sm">
                  {ev.event_type === 'auto_block' ? (
                    <Ban className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                  ) : (
                    <ShieldAlert className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-white">
                      <span className="font-mono">{ev.ip}</span>{' '}
                      <span className="text-neutral-500">·</span>{' '}
                      <span className="text-neutral-400">{ev.event_type}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {ev.meta?.category && (
                        <>[{ev.meta.category}] hit {ev.meta.hits}/{ev.meta.limit} in {ev.meta.window_seconds}s · </>
                      )}
                      {ev.meta?.path && <>{ev.meta.method} {ev.meta.path} · </>}
                      {formatDT(ev.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Whitelist */}
        <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-neutral-300 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            Whitelist (never blocked)
          </h2>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="whitelist-badges">
            {whitelist.map((ip) => (
              <span
                key={ip}
                className="text-xs font-mono px-2 py-1 rounded bg-neutral-900 border border-neutral-800 text-neutral-300"
              >
                {ip}
              </span>
            ))}
          </div>
          <p className="text-xs text-neutral-600 mt-3">
            To edit: set the <code>IP_BLOCKLIST_WHITELIST</code> env var
            (comma-separated) in backend/.env and restart.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
